import type { ApolloError } from "@apollo/client";
import { useCallback } from "react";
import { useRouter } from "solito/router";
import { v4 as uuid } from "uuid";
import { LinearGradient } from "@tamagui/linear-gradient";
import type { GetPersonalizedPromptsQuery } from "@medbillai/generated-graphql-types";
import { getTokenValue, ScrollView, Sparkles, XStack, YStack } from "@medbillai/ui";
import { CaseTypeSchema } from "@medbillai/schemas";
import { issuePreviewUrl } from "@medbillai/utils";
import { withAlpha } from "@medbillai/utils/color";
import { analyticsService } from "../../../lib/analytics";
import { useCaseStore } from "../../../lib/caseStore";
import { encodePreviewConfig } from "../../case/hooks/useCaseScreenParams";
import { createSystemMessageEvent, createUserChatEvent } from "../../case/utils/createPreviewEvents";
import { PersonalizedPrompt } from "./PersonalizedPrompt";
import { WidgetWrapper } from "./WidgetWrapper";

// On native, the string "transparent" parses to rgba(0,0,0,0), which makes
// gradients fade through black. Resolve $surface1 to its hex value and apply
// zero alpha so the gradient fades through the same color.
const surface1 = String(getTokenValue("$surface1", "color"));
const surface1Transparent = withAlpha(surface1, 0);
type PersonalizedPromptsProps = {
  prompts: NonNullable<GetPersonalizedPromptsQuery["getPersonalizedPrompts"]>["prompts"];
  error: ApolloError | undefined;
};
export function PersonalizedPrompts({
  prompts,
  error
}: PersonalizedPromptsProps) {
  const {
    push
  } = useRouter();
  const setPreviewData = useCaseStore(state => state.setPreviewData);
  const handlePromptPress = useCallback((prompt: GetPersonalizedPromptsQuery["getPersonalizedPrompts"]["prompts"][number]) => {
    const {
      id,
      text,
      response,
      caseType
    } = prompt;
    analyticsService.trackEvent("CaseIntakeStarted", {
      source: `personalized_prompt: ${text}`
    });

    // Generate storeKey and create preview data
    const storeKey = uuid();
    const events = [{
      event: createUserChatEvent(text),
      metadata: {} // USER message - no messageId needed
    }, {
      event: createSystemMessageEvent(response),
      metadata: {
        messageId: id
      } // SYSTEM message with messageId
    }];

    // Save data to store
    setPreviewData(storeKey, {
      events
    });

    // Navigate to preview case
    push(issuePreviewUrl({
      config: encodePreviewConfig({
        id: storeKey,
        // A stale bundle can receive enum values newer than itself; fall
        // back to OTHER rather than throwing when the prompt is tapped.
        caseType: CaseTypeSchema.catch("OTHER").parse(caseType),
        title: text,
        events: events
      })
    }));
  }, [push, setPreviewData]);
  if (error || prompts.length < 1) {
    return null;
  }
  return <WidgetWrapper title="Ask Granted" titleIcon={Sparkles} data-sentry-element="WidgetWrapper" data-sentry-component="PersonalizedPrompts" data-sentry-source-file="PersonalizedPrompts.tsx">
      <YStack position="relative" mx="$-sp2.5" data-sentry-element="YStack" data-sentry-source-file="PersonalizedPrompts.tsx">
        <ScrollView horizontal showsHorizontalScrollIndicator={false} data-sentry-element="ScrollView" data-sentry-source-file="PersonalizedPrompts.tsx">
          <XStack gap="$sp0.75" px="$sp2.5" data-sentry-element="XStack" data-sentry-source-file="PersonalizedPrompts.tsx">
            {prompts.map(prompt => <PersonalizedPrompt key={prompt.id} prompt={prompt} onPress={() => handlePromptPress(prompt)} />)}
          </XStack>
        </ScrollView>
        <LinearGradient position="absolute" left={0} top={0} bottom={0} width="$si2.5" // equal to the $-sp2.5 horizontal margin
      zIndex={1} start={{
        x: 0,
        y: 0
      }} end={{
        x: 1,
        y: 0
      }} locations={[0.1, 1]} colors={[surface1, surface1Transparent]} pointerEvents="none" data-sentry-element="LinearGradient" data-sentry-source-file="PersonalizedPrompts.tsx" />
        <LinearGradient position="absolute" right={0} top={0} bottom={0} width="$si2.5" // equal to the $-sp2.5 horizontal margin
      zIndex={1} start={{
        x: 0,
        y: 0
      }} end={{
        x: 1,
        y: 0
      }} locations={[0, 0.9]} colors={[surface1Transparent, surface1]} pointerEvents="none" data-sentry-element="LinearGradient" data-sentry-source-file="PersonalizedPrompts.tsx" />
      </YStack>
    </WidgetWrapper>;
}